import { CheckCircle, DownloadOutlined, Error as ErrorIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';

import CSVTemplate from 'src/assets/templates/admin_import_contacts.csv?url';
import CSVReader, { CSVReaderResult } from 'src/components/csv/csv-reader';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useTranslate } from 'src/locales';
import { useBulkContactImportMutation } from 'src/services/contacts/contacts.service';
import { getBulkImportErrorMessage } from 'src/services/contacts/contacts.utils';

interface ContactCSVRow {
  firstName: string;
  lastName: string;
  email: string;
  companyName?: string;
  specialInformation?: string;
  phoneNumber?: string;
  companyId?: string;
  ownerId?: string;
}

interface ImportResult {
  email: string;
  status: 'success' | 'failed';
  message?: string;
}

type Props = {
  organizationId?: string | undefined;
  open: boolean;
  onClose: VoidFunction;
};

enum ImportStep {
  UPLOAD = 'upload',
  PREVIEW = 'preview',
  RESULTS = 'results',
}

export default function BulkContactUploadModal({ organizationId, open, onClose }: Props) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const authContext = useAuthContext();

  const [bulkContactImport, { isLoading: isImporting }] = useBulkContactImportMutation();

  const [currentStep, setCurrentStep] = useState<ImportStep>(ImportStep.UPLOAD);
  const [parsedData, setParsedData] = useState<ContactCSVRow[]>([]);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);

  const [resultsPage, setResultsPage] = useState(0);
  const [resultsRowsPerPage, setResultsRowsPerPage] = useState(10);

  const expectedHeaders = useMemo(() => [
    'First Name',
    'Last Name',
    'Email',
    'Company Name',
    'Special Information',
    'Phone Number',
    'Company ID',
    'Owner ID',
  ], []);

  const handleCSVRead = useCallback((results: CSVReaderResult) => {
    setError(null);

    try {
      const { data } = results;

      if (!data || data.length === 0) {
        setError(t('contacts.bulk_import_modal.errors.empty_file'));
        return;
      }

      const headers = data[0] as string[];

      const hasAllRequiredHeaders = ['First Name', 'Last Name', 'Email'].every((header) =>
        headers.includes(header)
      );

      if (!hasAllRequiredHeaders) {
        setError(t('contacts.bulk_import_modal.errors.invalid_headers'));
        return;
      }

      const rows: ContactCSVRow[] = [];

      for (let i = 1; i < data.length; i++) {
        const row = data[i] as string[];

        if (!row || row.every((cell) => !cell || cell.trim() === '')) {
          // eslint-disable-next-line no-continue
          continue;
        }

        const firstName = row[headers.indexOf('First Name')]?.trim() || '';
        const lastName = row[headers.indexOf('Last Name')]?.trim() || '';
        const email = row[headers.indexOf('Email')]?.trim() || '';

        if (!firstName || !lastName || !email) {
          // eslint-disable-next-line no-continue
          continue;
        }

        rows.push({
          firstName,
          lastName,
          email,
          companyName: row[headers.indexOf('Company Name')]?.trim() || '',
          specialInformation: row[headers.indexOf('Special Information')]?.trim() || '',
          phoneNumber: row[headers.indexOf('Phone Number')]?.trim() || '',
          companyId: row[headers.indexOf('Company ID')]?.trim() || '',
          ownerId: row[headers.indexOf('Owner ID')]?.trim() || '',
        });
      }

      if (rows.length === 0) {
        setError(t('contacts.bulk_import_modal.errors.no_valid_rows'));
        return;
      }

      setParsedData(rows);
      setCurrentStep(ImportStep.PREVIEW);
    } catch (err) {
      console.error('CSV parsing error:', err);
      setError(t('contacts.bulk_import_modal.errors.parse_failed'));
    }
  }, [t]);

  const handleImport = useCallback(async () => {
    setError(null);

    try {
      const importData = parsedData.map((contact) => ({
        first_name: contact.firstName,
        last_name: contact.lastName,
        email: contact.email,
        company_name: contact.companyName || undefined,
        special_information: contact.specialInformation || undefined,
        phone_number: contact.phoneNumber || undefined,
        company_id: contact.companyId || undefined,
        owner_id: contact.ownerId || undefined,
      }));

      const response = await bulkContactImport({
        data: {
          organization_id: organizationId,
          contacts: importData,
        },
      }).unwrap();

      setImportResults(response);
      setCurrentStep(ImportStep.RESULTS);
      enqueueSnackbar(t('contacts.bulk_import_modal.import_success'), { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(getBulkImportErrorMessage(err), { variant: 'error' });
    }
  }, [parsedData, t, bulkContactImport, organizationId, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    setCurrentStep(ImportStep.UPLOAD);
    setParsedData([]);
    setImportResults([]);
    setError(null);
    setPreviewPage(0);
    setResultsPage(0);
    onClose();
  }, [onClose]);

  const handleBackToUpload = useCallback(() => {
    setCurrentStep(ImportStep.UPLOAD);
    setParsedData([]);
    setError(null);
    setPreviewPage(0);
  }, []);

  const resultStats = useMemo(() => {
    const successful = importResults.filter((r) => r.status === 'success').length;
    const failed = importResults.filter((r) => r.status === 'failed').length;
    return { successful, failed, total: importResults.length };
  }, [importResults]);

  const renderUploadStep = () => (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info">
        {t('contacts.bulk_import_modal.upload_instructions')}
      </Alert>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('contacts.bulk_import_modal.required_fields')}:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {expectedHeaders.slice(0, 3).map((header) => (
            <Chip key={header} label={header} size="small" color="primary" />
          ))}
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t('contacts.bulk_import_modal.optional_fields')}:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {expectedHeaders.slice(3).map((header) => (
            <Chip key={header} label={header} size="small" variant="outlined" />
          ))}
        </Stack>
      </Box>

      <CSVReader onRead={handleCSVRead} />
    </Stack>
  );

  const renderPreviewStep = () => {
    const paginatedData = parsedData.slice(
      previewPage * previewRowsPerPage,
      previewPage * previewRowsPerPage + previewRowsPerPage
    );

    return (
      <Stack spacing={2}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="success">
          {t('contacts.bulk_import_modal.preview_success', { count: parsedData.length })}
        </Alert>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('contacts.bulk_import_modal.table.first_name')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.last_name')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.email')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.company')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.special_info')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.phone_number')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.company_id')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.owner_id')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell>{row.firstName}</TableCell>
                  <TableCell>{row.lastName}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.companyName || '-'}</TableCell>
                  <TableCell>
                    {row.specialInformation ? (
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {row.specialInformation}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{row.phoneNumber || '-'}</TableCell>
                  <TableCell>{row.companyId || '-'}</TableCell>
                  <TableCell>{row.ownerId || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={parsedData.length}
          page={previewPage}
          onPageChange={(_, newPage) => setPreviewPage(newPage)}
          rowsPerPage={previewRowsPerPage}
          onRowsPerPageChange={(e) => {
            setPreviewRowsPerPage(parseInt(e.target.value, 10));
            setPreviewPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Stack>
    );
  };

  const renderResultsStep = () => {
    const paginatedResults = importResults.slice(
      resultsPage * resultsRowsPerPage,
      resultsPage * resultsRowsPerPage + resultsRowsPerPage
    );

    return (
      <Stack spacing={2}>
        <Alert severity={resultStats.failed > 0 ? 'warning' : 'success'}>
          {t('contacts.bulk_import_modal.results_summary', {
            successful: resultStats.successful,
            failed: resultStats.failed,
            total: resultStats.total,
          })}
        </Alert>

        <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t('contacts.bulk_import_modal.table.status')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.email')}</TableCell>
                <TableCell>{t('contacts.bulk_import_modal.table.result')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((result, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    {result.status === 'success' ? (
                      <CheckCircle color="success" fontSize="small" />
                    ) : (
                      <ErrorIcon color="error" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell>{result.email}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={result.status === 'success' ? 'success.main' : 'error.main'}
                    >
                      {result.message}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={importResults.length}
          page={resultsPage}
          onPageChange={(_, newPage) => setResultsPage(newPage)}
          rowsPerPage={resultsRowsPerPage}
          onRowsPerPageChange={(e) => {
            setResultsRowsPerPage(parseInt(e.target.value, 10));
            setResultsPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {
          currentStep === ImportStep.UPLOAD && (
            <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
              <Typography>
                {t('contacts.bulk_import_modal.title')}
              </Typography>

              <Button
                startIcon={<DownloadOutlined />}
                href={CSVTemplate}
                download="Contact Import Template.csv"
              >
                {t('candidates.invites.candidate_invite_modal.download_template')}
              </Button>
            </Stack>
          )
        }
        {currentStep === ImportStep.PREVIEW && t('contacts.bulk_import_modal.preview_title')}
        {currentStep === ImportStep.RESULTS && t('contacts.bulk_import_modal.results_title')}
      </DialogTitle>

      <DialogContent>
        {currentStep === ImportStep.UPLOAD && renderUploadStep()}
        {currentStep === ImportStep.PREVIEW && renderPreviewStep()}
        {currentStep === ImportStep.RESULTS && renderResultsStep()}
      </DialogContent>

      <DialogActions>
        {currentStep === ImportStep.UPLOAD && (
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
        )}

        {currentStep === ImportStep.PREVIEW && (
          <>
            <Button onClick={handleBackToUpload}>{t('common.back')}</Button>
            <LoadingButton
              variant="contained"
              onClick={handleImport}
              loading={isImporting}
            >
              {t('contacts.bulk_import_modal.import_button')}
            </LoadingButton>
          </>
        )}

        {currentStep === ImportStep.RESULTS && (
          <Button variant="contained" onClick={handleClose}>
            {t('common.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}